import React, {useEffect, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import L from 'leaflet';
import {MapContainer, Marker, TileLayer, Tooltip, useMap} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import {infoColor, dangerColor} from 'assets/jss/main';

const DEFAULT_CENTER = [46.6, 2.4]; // France, used only while nothing has been geolocated yet
const DEFAULT_ZOOM = 6;

// The API sends null coordinates for relay points Mondial Relay could not geolocate; anything that is
// not a finite number would give Leaflet an invalid LatLng and break fitBounds/setView.
const hasCoordinates = relayPoint => Number.isFinite(relayPoint?.latitude) && Number.isFinite(relayPoint?.longitude);
const nullableNumber = PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]);

/**
 * Leaflet's default marker relies on image assets whose URLs break under webpack, so markers are
 * plain HTML pins numbered like the list on the left. The selected one is highlighted.
 */
function buildIcon(index, {selected, current}) {
    const background = selected ? dangerColor : current ? '#333' : infoColor;

    return L.divIcon({
        className: 'relay-point-marker',
        iconSize: [28, 36],
        iconAnchor: [14, 36],
        tooltipAnchor: [0, -30],
        html: `<div style="width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${background};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;">
            <span style="transform:rotate(45deg);color:#fff;font-size:12px;font-weight:700;">${index + 1}</span>
        </div>`,
    });
}

function MapController({relayPoints, fitToResults, onMoveEnd}) {
    const map = useMap();
    const programmaticMove = useRef(false);
    const onMoveEndRef = useRef(onMoveEnd);
    onMoveEndRef.current = onMoveEnd;

    useEffect(() => {
        const handleMoveEnd = () => {
            if (programmaticMove.current) {
                programmaticMove.current = false;

                return;
            }
            if (!onMoveEndRef.current) {
                return;
            }
            const center = map.getCenter();
            onMoveEndRef.current({latitude: center.lat, longitude: center.lng, zoom: map.getZoom()});
        };
        map.on('moveend', handleMoveEnd);

        return () => map.off('moveend', handleMoveEnd);
    }, [map]);

    useEffect(() => {
        if (!fitToResults) {
            return;
        }
        const points = relayPoints.map(relayPoint => [relayPoint.latitude, relayPoint.longitude]);
        if (points.length === 0) {
            return;
        }
        programmaticMove.current = true;
        if (points.length === 1) {
            map.setView(points[0], 15);

            return;
        }
        map.fitBounds(points, {padding: [30, 30], maxZoom: 16});
    }, [map, relayPoints, fitToResults]);

    return null;
}

export default function RelayPointMap({relayPoints, selectedId, currentRelayPointId, onSelect, height, fitToResults, onMoveEnd, overlay}) {
    const located = useMemo(() => relayPoints.filter(hasCoordinates), [relayPoints]);

    return (
        <div style={{position: 'relative', height, width: '100%'}}>
            {overlay && (
                <div style={{position: 'absolute', top: 12, left: 0, right: 0, zIndex: 1000, display: 'flex', justifyContent: 'center', pointerEvents: 'none'}}>
                    <div style={{pointerEvents: 'auto'}}>{overlay}</div>
                </div>
            )}
        <MapContainer
            center={DEFAULT_CENTER}
            zoom={DEFAULT_ZOOM}
            scrollWheelZoom={true}
            style={{height, width: '100%', borderRadius: 8}}
        >
            <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapController relayPoints={located} fitToResults={fitToResults} onMoveEnd={onMoveEnd} />
            {located.map(relayPoint => {
                const index = relayPoints.indexOf(relayPoint);

                return (
                    <Marker
                        key={relayPoint.id}
                        position={[relayPoint.latitude, relayPoint.longitude]}
                        icon={buildIcon(index, {selected: relayPoint.id === selectedId, current: relayPoint.id === currentRelayPointId})}
                        zIndexOffset={relayPoint.id === selectedId ? 1000 : 0}
                        keyboard={false}
                        eventHandlers={{click: () => onSelect(relayPoint)}}
                    >
                        <Tooltip direction="top">
                            <strong>{relayPoint.name}</strong>
                            <br />
                            {relayPoint.address}, {relayPoint.postalCode} {relayPoint.city}
                        </Tooltip>
                    </Marker>
                );
            })}
        </MapContainer>
        </div>
    );
}

RelayPointMap.propTypes = {
    relayPoints: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        address: PropTypes.string,
        postalCode: PropTypes.string,
        city: PropTypes.string,
        latitude: nullableNumber,
        longitude: nullableNumber,
    })).isRequired,
    selectedId: PropTypes.string,
    currentRelayPointId: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    height: PropTypes.number,
    fitToResults: PropTypes.bool,
    onMoveEnd: PropTypes.func,
    overlay: PropTypes.node,
};

RelayPointMap.defaultProps = {
    selectedId: null,
    currentRelayPointId: null,
    height: 520,
    fitToResults: true,
    onMoveEnd: null,
    overlay: null,
};
